import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import go_n from '../../Images/go_n.gif';
import go_o from '../../Images/go_o.gif';
import close_n from '../../Images/close_n.gif';
import close_o from '../../Images/close_o.gif';
import popup_bg from '../../Common/Images/ImagesForAlert/popup_bg1.png';
import alert_close_n from '../../Common/Images/ImagesForAlert/alert_close_n.png';
import alert_close_o from '../../Common/Images/ImagesForAlert/alert_close_o.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface UsageEntry {
    SchoolCode: string;
    Name: string;
    'No.Of.Students': number;
    UsedBy: number;
    'Usage(Hrs)': number;
}

interface DropdownOption {
    value: string;
    label: string;
}

interface Props {
    onBack?: () => void;
}

const styles = {
    fullScreenWindow: {
        position: 'fixed' as 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'Azure',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column' as 'column'
    },
    popupHeader: {
        backgroundImage: `url(${popup_bg})`,
        backgroundSize: '100% 100%',
        height: '30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '10px',
        flexShrink: 0
    },
    popupTitle: {
        color: 'white',
        fontWeight: 'bold' as 'bold',
        fontSize: '11pt',
        fontFamily: 'Arial'
    },
    container: {
        position: 'relative' as 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as 'column',
        fontFamily: 'Arial, sans-serif',
        fontSize: '10pt',
        backgroundColor: 'Azure',
        overflow: 'hidden'
    },
    headerSection: {
        backgroundColor: 'Azure',
        flexShrink: 0,
        padding: '10px 15px',
        overflow:'auto'
    },
    titleBar: {
        textAlign: 'center' as 'center',
        fontWeight: 'bold' as 'bold',
        fontSize: '12pt',
        padding: '10px',
        // marginBottom: '10px',
        border: "1px solid black",
        borderBottom: "none"
    },
    controlsTable: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        backgroundColor: 'Azure',
        border: '1px solid black'
    },
    controlCell: {
        padding: '10px',
        verticalAlign: 'middle' as 'middle',
        textAlign: 'left' as 'left'
    },
    label: {
        fontWeight: 'bold' as 'bold',
        marginRight: '10px'
    ,
    fontSize:"12pt"
    },
    input: {
        border: '1px solid #003385',
        padding: '2px',
        fontSize: '12pt',
        fontFamily: 'Calibri',
        width: '180px'
    },
    radioGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        whiteSpace: 'nowrap' as 'nowrap',
        fontSize:"12pt"
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
        padding: '15px 20px',
        width: '100%',
        textAlign: 'left' as const
    },
    dataTable: {
        borderCollapse: 'collapse' as const,
        fontSize: '10pt',
        border: '1px solid #80a0ff',
        display: 'inline-table',
        whiteSpace: 'nowrap' as const
    },
    th: {
        backgroundColor: '#c7ddfd',
        color: '#2859ac',
        fontSize: '10pt',
        padding: '5px 10px',
        border: '1px solid #80a0ff',
        textAlign: 'left' as const,
        position: 'sticky' as const,
        top: 0,
        fontWeight: 'normal' as const,
        whiteSpace: 'nowrap' as const
    },
    td: {
        padding: '8px 10px',
        borderRight: '1px solid #80a0ff',
        fontSize: '10pt',
        textAlign: 'left' as const,
        whiteSpace: 'nowrap' as const
    },
};

const EL_SchoolUsage: React.FC<Props> = ({ onBack }) => {
    const [searchParams] = useSearchParams();
    const calledFrom = searchParams.get('CalledFrom') || '';

    const formatDate = (date: Date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    };

    const today = new Date();
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const [dateOption, setDateOption] = useState<string>('CurrentMonth');
    const [fromDate, setFromDate] = useState(formatDate(currentMonthStart));
    const [toDate, setToDate] = useState(formatDate(currentMonthEnd));

    const [countries, setCountries] = useState<DropdownOption[]>([{ value: 'ALL', label: 'ALL' }]);
    const [selectedCountry, setSelectedCountry] = useState<string>('ALL');

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<UsageEntry[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    const [isGoHovered, setIsGoHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);

    useEffect(() => {
        const fetchCountries = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/school-usage/filters/`);
                if (response.ok) {
                    const data = await response.json();
                    const cOpts = data.countries.map((c: string) => ({ value: c, label: c }));
                    setCountries([{ value: 'ALL', label: 'ALL' }, ...cOpts]);
                }
            } catch (err) { console.error("Error fetching countries", err); }
            finally { setLoading(false); }
        };
        fetchCountries();
    }, []);

    const handleDateOptionChange = (option: string) => {
        setDateOption(option);
        if (option === 'CurrentMonth') {
            setFromDate(formatDate(new Date(today.getFullYear(), today.getMonth(), 1)));
            setToDate(formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
        } else if (option === 'LastMonth') {
            setFromDate(formatDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
            setToDate(formatDate(new Date(today.getFullYear(), today.getMonth(), 0)));
        }
    };

    const handleGoClick = async () => {
        if (!dateOption) {
            setAlertInfo({ show: true, message: 'Please select a Month or Period to continue' });
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                startDate: fromDate,
                endDate: toDate,
                country: selectedCountry,
            });

            const response = await fetch(`${API_BASE_URL}api/school-usage/generate/?${params.toString()}`);
            if (!response.ok) throw new Error("Failed to load report data.");

            const data = await response.json();
            setReportData(data.data || []);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.fullScreenWindow}>
            <div style={styles.popupHeader}>
                <span style={styles.popupTitle}>School Usage Report</span>
                <img
                    src={alert_close_n}
                    alt="Close"
                    style={{ cursor: 'pointer', height: '30px', width: '62px' }}
                    onMouseOver={(e) => e.currentTarget.src = alert_close_o}
                    onMouseOut={(e) => e.currentTarget.src = alert_close_n}
                    onClick={() => {
                        if (onBack) onBack();
                        else if (calledFrom) window.close();
                        else window.history.back();
                    }}
                />
            </div>

            <div style={styles.container}>
                <div style={styles.headerSection}>
                    <div style={styles.titleBar}>School Usage</div>

                    <table style={styles.controlsTable}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.controlCell, width: '20%' }}>
                                    <span style={styles.label}>Country:</span>
                                    <select style={styles.input} value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}>
                                        {countries.map((c, i) => <option key={i} value={c.value}>{c.label}</option>)}
                                    </select>
                                </td>
                                <td style={{ ...styles.controlCell, width: '13%' }}>
                                    <div style={styles.radioGroup}>
                                        <input type="radio" id="currMonth" style={{margin:0}} checked={dateOption === 'CurrentMonth'} onChange={() => handleDateOptionChange('CurrentMonth')} />
                                        <label htmlFor="currMonth">Current Month</label>
                                    </div>
                                </td>
                                <td style={{ ...styles.controlCell, width: '11%' }}>
                                    <div style={styles.radioGroup}>
                                        <input type="radio" id="lastMonth" style={{margin:0}} checked={dateOption === 'LastMonth'} onChange={() => handleDateOptionChange('LastMonth')} />
                                        <label htmlFor="lastMonth">Last Month</label>
                                    </div>
                                </td>
                                <td style={{ ...styles.controlCell, width: '10%' }}>
                                    <div style={styles.radioGroup}>
                                        <input type="radio" id="period" style={{margin:0}} checked={dateOption === 'Period'} onChange={() => handleDateOptionChange('Period')} />
                                        <label htmlFor="period">Period</label>
                                    </div>
                                </td>
                                <td style={{ ...styles.controlCell, width: '28%', display: dateOption === 'Period' ? 'table-cell' : 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize:"12pt" }}>
                                        <span>From:</span>
                                        <input type="date" style={styles.input} value={fromDate} onChange={e => setFromDate(e.target.value)} />
                                        <span>To:</span>
                                        <input type="date" style={styles.input} value={toDate} onChange={e => setToDate(e.target.value)} />
                                    </div>
                                </td>

                                <td style={{ ...styles.controlCell, textAlign: 'right', borderRight: 'none', width: '20%' }}>
                                    <img
                                        src={isGoHovered ? go_o : go_n}
                                        alt="Go"
                                        onClick={handleGoClick}
                                        onMouseEnter={() => setIsGoHovered(true)}
                                        onMouseLeave={() => setIsGoHovered(false)}
                                        style={{ cursor: 'pointer', marginRight: '15px' }}
                                    />
                                    <img
                                        src={isCloseHovered ? close_o : close_n}
                                        alt="Close"
                                        onClick={() => {
                                            if (onBack) onBack();
                                            else if (calledFrom) window.close();
                                            else window.history.back();
                                        }}
                                        onMouseEnter={() => setIsCloseHovered(true)}
                                        onMouseLeave={() => setIsCloseHovered(false)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {loading && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Loading /></div>}
                {error && <div style={{ color: 'red', padding: '10px' }}>{error}</div>}

                <div style={styles.tableContainer}>
                    {reportData.length > 0 && (
                        <table style={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    <th style={styles.th}>School Code</th>
                                    <th style={styles.th}>School Name</th>
                                    <th style={styles.th}>No.of.Students<br />&nbsp;&nbsp;&nbsp;in School</th>
                                    <th style={styles.th}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;UsedBy<br />No.of.Students</th>
                                    <th style={{...styles.th, width:"100%"}}>Total Usage(Hours)</th>
                                </tr>
                            </thead>
                            <tbody>

                                {reportData.map((row, idx) => (
                                    <tr
                                        key={idx}
                                        style={{
                                            backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F4FAFF'
                                        }}
                                    >
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{idx + 1}</td>
                                        <td style={styles.td}>{row.SchoolCode}</td>
                                        <td style={styles.td}>{row.Name}</td>
                                        <td style={styles.td}>{row['No.Of.Students'] || 0}</td>
                                        <td style={styles.td}>{row.UsedBy || 0}</td>
                                        <td style={{ ...styles.td, textAlign: 'left' }}>
                                            {(Number(row['Usage(Hrs)']))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {alertInfo.show && (
                    <CustomAlert
                        message={alertInfo.message}
                        onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                    />
                )}
            </div>
        </div>
    );
};

export default EL_SchoolUsage;